import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { 
  Search, Filter, Eye, CheckCircle, XCircle, Download, 
  FileText, Clock, DollarSign, AlertTriangle, ChevronRight,
  X, RefreshCw, PoundSterling, CreditCard, Send, Trash2
} from 'lucide-react';
import api from '../../utils/api';
import { checkStripeMinimum, getMinimumAmountError } from '../../utils/stripeMinimums';
import MobileInvoicePreview from './MobileInvoicePreview';
import EarlyPaymentBanner from '../shared/EarlyPaymentBanner';
import { generateInvoicePDF } from '../../utils/pdfGenerator';
import { downloadInvoice } from '../../utils/invoiceDownload';
import PlanBadge from '../common/PlanBadge';

const isNumberHighlight = (value) => typeof value === 'string' && value.startsWith('number:');
const extractHighlightValue = (value) =>
  isNumberHighlight(value) ? value.slice(7) : value;

const cleanFieldValue = (value) => {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  return value;
};

const filterUndefinedFields = (obj = {}) => {
  const entries = Object.entries(obj)
    .map(([key, value]) => [key, cleanFieldValue(value)])
    .filter(([, value]) => value !== undefined);
  return entries.length ? Object.fromEntries(entries) : {};
};

const normalizeAddressFields = (entity = {}) => {
  if (!entity || typeof entity !== 'object') {
    return {};
  }

  const line1 =
    entity.address ??
    entity.address_line1 ??
    entity.addressLine1 ??
    entity.street ??
    entity.street1 ??
    entity.line1 ??
    entity.address1;

  const line2 =
    entity.address_line2 ??
    entity.addressLine2 ??
    entity.street2 ??
    entity.street_2 ??
    entity.line2 ??
    entity.address2;

  const combinedAddress = entity.address
    ? entity.address
    : [line1, line2].filter(Boolean).join(', ');

  const city =
    entity.city ??
    entity.town ??
    entity.locality ??
    entity.village ??
    entity.suburb ??
    entity.cityName;

  const state =
    entity.state ??
    entity.county ??
    entity.region ??
    entity.province ??
    entity.stateProvince ??
    entity.state_name;

  const zip =
    entity.zipCode ??
    entity.zip_code ??
    entity.postalCode ??
    entity.postal_code ??
    entity.postcode ??
    entity.post_code ??
    entity.zip ??
    entity.pinCode;

  const country =
    entity.country ??
    entity.countryName ??
    entity.country_code ??
    entity.countryCode ??
    entity.countryname;

  return filterUndefinedFields({
    address: combinedAddress,
    addressLine1: line1,
    addressLine2: line2,
    city,
    state,
    zipCode: zip,
    country,
  });
};

const buildContactDetails = (entity) => {
  if (!entity || typeof entity !== 'object') {
    return {};
  }

  const normalizedAddress = normalizeAddressFields(entity);

  const firstName =
    entity.firstName ??
    entity.firstname ??
    entity.givenName ??
    entity.first_name;
  const lastName =
    entity.lastName ??
    entity.lastname ??
    entity.familyName ??
    entity.surname ??
    entity.last_name;
  const companyName =
    entity.companyName ??
    entity.company ??
    entity.businessName ??
    entity.organisation ??
    entity.organization ??
    entity.accountName;

  const directName =
    entity.name ?? entity.fullName ?? entity.displayName ?? entity.contactName;

  const email =
    entity.email ?? entity.contactEmail ?? entity.emailAddress ?? entity.email_address;

  const phone =
    entity.phone ??
    entity.phoneNumber ??
    entity.contactNumber ??
    entity.telephone ??
    entity.mobile ??
    entity.mobileNumber;

  const derivedName = [directName, companyName, [firstName, lastName].filter(Boolean).join(' ').trim(), email]
    .map(cleanFieldValue)
    .find((value) => value !== undefined);

  const id =
    entity.id ??
    entity.userId ??
    entity.receiverId ??
    entity.recipientId ??
    entity.contactId ??
    entity.externalId ??
    entity.uuid ??
    entity.user_id ??
    entity.receiver_id;

  return filterUndefinedFields({
    id,
    companyName,
    firstName,
    lastName,
    name: derivedName,
    email,
    phone,
    ...normalizedAddress,
  });
};

const MobileInvoices = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('received'); // 'received' or 'sent'
  const activeTabRef = useRef('received');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [actionModal, setActionModal] = useState({ open: false, type: '', invoice: null });
  const [rejectReason, setRejectReason] = useState('');
  const [approvingInvoices, setApprovingInvoices] = useState(new Set()); // Track invoices being approved
  const [approvalErrors, setApprovalErrors] = useState(new Map()); // Track invoices with confirmed approval failures
  const lastHighlightRef = useRef(null);
  const highlightQueue = useRef([]);
  const currentInvoicesTab = useRef('received');
  const [initialHighlightHandled, setInitialHighlightHandled] = useState(false);
  const invoiceRetryRef = useRef({ received: false, sent: false });
  const fetchRetryTimeoutRef = useRef(null);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const highlightParam = searchParams.get('highlight');

    const targetTab =
      tabParam && (tabParam === 'sent' || tabParam === 'received')
        ? tabParam
        : activeTab;

    if (highlightParam && lastHighlightRef.current !== highlightParam) {
      lastHighlightRef.current = highlightParam;
      highlightQueue.current = [{
        id: highlightParam,
        tab: targetTab
      }];
      setInitialHighlightHandled(false);
    }

    if (!highlightParam) {
      highlightQueue.current = [];
      lastHighlightRef.current = null;
      setInitialHighlightHandled(false);
    }

    if (highlightParam && !initialHighlightHandled && targetTab !== activeTab) {
      setActiveTab(targetTab);
      setInitialHighlightHandled(true);
    }
  }, [searchParams, activeTab, initialHighlightHandled]);

  const fetchInvoices = useCallback(async (tabOverride) => {
    const tab = tabOverride || activeTabRef.current;
    try {
      setLoading(true);
      const role = tab === 'received' ? 'receiver' : 'sender';
      const response = await api.get('/invoices', {
        params: { role },
        __skipAuthTokenPurge: true,
      });
      
      console.log('📱 MobileInvoices - API Response:', response.data);
      console.log('📱 MobileInvoices - First invoice raw:', response.data.data?.invoices?.[0]);
      
      if (response.data.data && response.data.data.invoices) {
        const transformedInvoices = response.data.data.invoices.map(invoice => {
          const senderDetails = buildContactDetails(invoice.sender);
          const receiverDetails = buildContactDetails(invoice.receiver);
          const guestDetails = buildContactDetails(invoice.guestRecipient);
          const recipientDetails = Object.keys(receiverDetails).length > 0 ? receiverDetails : guestDetails;

          const fallbackReceiverName = invoice.receiverId
            ? (
              invoice.receiver?.companyName ||
              [invoice.receiver?.firstName, invoice.receiver?.lastName].filter(Boolean).join(' ').trim()
            )
            : (
              invoice.guestRecipient?.companyName ||
              [invoice.guestRecipient?.firstName, invoice.guestRecipient?.lastName].filter(Boolean).join(' ').trim()
            );

          const recipientName =
            recipientDetails.name ||
            fallbackReceiverName ||
            invoice.receiver?.email ||
            invoice.guestRecipient?.email ||
            'Unknown recipient';

          const supplier = Object.keys(senderDetails).length > 0
            ? {
                name: senderDetails.name || senderDetails.companyName || invoice.sender?.companyName || 'Unknown sender',
                company: senderDetails.companyName,
                email: senderDetails.email,
                ...senderDetails,
              }
            : null;

          const customerBase = filterUndefinedFields({
            name: recipientName,
            company: recipientDetails.companyName,
            email: recipientDetails.email,
          });

          const customer = Object.keys(recipientDetails).length > 0
            ? { ...customerBase, ...recipientDetails }
            : Object.keys(customerBase).length > 0
              ? customerBase
              : null;

          const receiverForPreview = Object.keys(receiverDetails).length > 0
            ? receiverDetails
            : Object.keys(guestDetails).length > 0
              ? { ...guestDetails, isGuest: true }
              : null;

          const guestRecipientDetails = Object.keys(guestDetails).length > 0
            ? { ...guestDetails, isGuest: true }
            : null;

          const senderForPreview = Object.keys(senderDetails).length > 0 ? senderDetails : null;

          return {
            id: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            invoiceId: invoice.invoiceId,
            supplier,
            customer,
            amount: invoice.total,
            currency: invoice.currency || 'GBP',
            status: invoice.status,
            approvalStatus: invoice.approvalStatus,
            dueDate: invoice.dueDate,
            createdAt: invoice.createdAt,
            description: invoice.description,
            items: invoice.items || [],
            lineItems: invoice.items || [],
            sender: senderForPreview,
            receiver: receiverForPreview,
            guestRecipient: guestRecipientDetails,
            isGuestInvoice: !invoice.receiverId && !!guestRecipientDetails, // Match desktop logic
            discounts: invoice.discounts || invoice.earlyPaymentDiscounts || [],
            rejectionReason: invoice.rejectionReason,
            rejectReason: invoice.rejectReason,
            discountAmount: invoice.discountAmount,
            discountSourceInvoice: invoice.discountSourceInvoice,
            refundAmount: invoice.refundAmount,
            refundStatus: invoice.refundStatus,
            refundDate: invoice.refundDate,
            attachments: invoice.metadata?.attachments || [],
            tax: invoice.tax,
            subtotal: invoice.subtotal,
            metadata: invoice.metadata
          };
        });
        setInvoices(transformedInvoices);
      } else {
        setInvoices([]);
      }
      currentInvoicesTab.current = tab;
      invoiceRetryRef.current[tab] = false;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      const status = error?.response?.status;
      const code = error?.response?.data?.code;
      const details = error?.response?.data?.details || error?.response?.data?.message;

      const isTransientInvalidToken =
        status === 403 &&
        code === 'INVALID_TOKEN' &&
        typeof details === 'string' &&
        details.toLowerCase().includes('connection terminated unexpectedly');

      if (isTransientInvalidToken) {
        const tabKey = tab;
        if (!invoiceRetryRef.current[tabKey]) {
          invoiceRetryRef.current[tabKey] = true;
          if (fetchRetryTimeoutRef.current) {
            clearTimeout(fetchRetryTimeoutRef.current);
          }
          fetchRetryTimeoutRef.current = setTimeout(() => {
            fetchInvoices(tabKey);
          }, 750);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    activeTabRef.current = activeTab;
    invoiceRetryRef.current[activeTab] = false;
    fetchInvoices(activeTab);
  }, [activeTab, fetchInvoices]);

  useEffect(() => {
    return () => {
      if (fetchRetryTimeoutRef.current) {
        clearTimeout(fetchRetryTimeoutRef.current);
      }
    };
  }, []);

  const handleApprove = async () => {
    const invoiceId = selectedInvoice.id;
    
    // Immediately disable button by adding to approving set
    setApprovingInvoices(prev => new Set(prev).add(invoiceId));
    setApprovalErrors(prev => {
      const newMap = new Map(prev);
      newMap.delete(invoiceId); // Clear any previous error
      return newMap;
    });
    
    try {
      setLoading(true);
      
      // Send approval with payment authorization
      const response = await api.post(`/api/invoices/${invoiceId}/approve`, {
        paymentAuthorization: true
      });
      
      if (response.data.success) {
        const invoiceData = response.data.data?.invoice;
        const finalStatus = invoiceData?.status || 'approved';
        const wasProcessedImmediately = invoiceData?.processedImmediately || false;
        
        // Update local state - invoice might be 'paid' if it was overdue
        setInvoices(prev => prev.map(inv => 
          inv.id === invoiceId ? { 
            ...inv, 
            status: finalStatus,
            approvalStatus: 'approved'
          } : inv
        ));
        
        // Remove from approving set since it succeeded
        setApprovingInvoices(prev => {
          const newSet = new Set(prev);
          newSet.delete(invoiceId);
          return newSet;
        });
        
        setActionModal({ open: false, type: '', invoice: null });
        setSelectedInvoice(null);
        
        // Show appropriate success message based on what happened
        if (finalStatus === 'paid') {
          if (wasProcessedImmediately) {
            alert('✅ Invoice approved and payment processed successfully! The invoice was overdue, so payment was collected immediately.');
          } else {
            alert('✅ Invoice approved and payment processed successfully!');
          }
        } else if (response.data.warning) {
          alert(`⚠️ ${response.data.warning}`);
        } else {
          alert('✅ Invoice approved successfully! Payment will be collected on the due date.');
        }
        
        // Refresh invoices to get updated data
        await fetchInvoices();
      } else {
        // Approval failed - check Stripe to confirm before re-enabling
        await checkApprovalFailureAndReenable(invoiceId);
        alert('Failed to approve invoice: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error approving invoice:', error);
      
      // Check if this is a timeout or network error (might have succeeded)
      const isTimeout = error.code === 'ECONNABORTED' || 
                       error.message?.includes('timeout') ||
                       error.response?.status === 504;
      
      if (isTimeout) {
        // Timeout - check if approval actually succeeded in Stripe before re-enabling
        console.log('⚠️ Approval request timed out - checking if approval succeeded in Stripe...');
        await checkApprovalFailureAndReenable(invoiceId);
        alert('Approval request timed out. Please check the invoice status - the approval may have succeeded. If the invoice still shows as pending, you can try again.');
      } else {
        // Confirmed error - check Stripe to confirm failure before re-enabling
        await checkApprovalFailureAndReenable(invoiceId);
        
        if (error.response?.status === 400) {
          if (error.response.data.message?.includes('linked bank account')) {
            alert('You must have a linked bank account to approve invoices. Please link your bank account in Settings first.');
          } else if (error.response.data.message?.includes('No payment method')) {
            alert('No payment method found. Please update your payment details in Settings before approving overdue invoices.');
          } else {
            alert('Invoice cannot be approved: ' + error.response.data.message);
          }
        } else if (error.response?.status === 403) {
          alert('You are not authorized to approve this invoice.');
        } else if (error.response?.status === 404) {
          alert('Invoice not found.');
        } else if (error.response?.data?.details) {
          alert('Payment processing error: ' + error.response.data.details);
        } else {
          alert('Error approving invoice. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Check if approval actually failed in Stripe before re-enabling the button
  const checkApprovalFailureAndReenable = async (invoiceId) => {
    try {
      // Fetch latest invoice status from backend
      const invoiceResponse = await api.get(`/invoices/${invoiceId}`);
      const latestInvoice = invoiceResponse.data.data || invoiceResponse.data;
      
      // Check if invoice was actually approved/paid (might have succeeded despite error)
      const isActuallyApproved = latestInvoice.status === 'approved' || 
                                 latestInvoice.status === 'paid' ||
                                 latestInvoice.approvalStatus === 'approved' ||
                                 latestInvoice.approvalStatus === 'auto_approved';
      
      if (isActuallyApproved) {
        // Approval actually succeeded - don't re-enable button, update UI
        console.log('✅ Approval actually succeeded despite error - invoice is approved');
        setApprovingInvoices(prev => {
          const newSet = new Set(prev);
          newSet.delete(invoiceId);
          return newSet;
        });
        
        // Update local state
        setInvoices(prev => prev.map(inv => 
          inv.id === invoiceId ? { 
            ...inv, 
            status: latestInvoice.status,
            approvalStatus: latestInvoice.approvalStatus || 'approved'
          } : inv
        ));
        
        // Refresh to get latest data
        await fetchInvoices();
        return; // Don't re-enable button
      }
      
      // Confirmed failure - re-enable button
      console.log('❌ Approval confirmed failed - re-enabling approve button');
      setApprovingInvoices(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoiceId);
        return newSet;
      });
      setApprovalErrors(prev => {
        const newMap = new Map(prev);
        newMap.set(invoiceId, true);
        return newMap;
      });
    } catch (checkError) {
      console.error('Error checking approval status:', checkError);
      // If we can't check, re-enable button to allow retry
      setApprovingInvoices(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoiceId);
        return newSet;
      });
    }
  };

  const handlePayNow = async () => {
    try {
      setLoading(true);
      
      // Send pay now request
      const response = await api.post(`/api/invoices/${selectedInvoice.id}/pay-now`);
      
      if (response.data.success) {
        const invoiceData = response.data.data?.invoice;
        const finalStatus = invoiceData?.status || 'paid';
        
        // Update local state - invoice should be 'paid' after Pay Now
        setInvoices(prev => prev.map(inv => 
          inv.id === selectedInvoice.id ? { 
            ...inv, 
            status: finalStatus,
            approvalStatus: 'approved'
          } : inv
        ));
        
        setActionModal({ open: false, type: '', invoice: null });
        setSelectedInvoice(null);
        
        // Show success message
        alert(`✅ Payment processed successfully! Invoice ${invoiceData?.invoiceNumber || selectedInvoice.invoiceNumber} has been paid.`);
        
        // Refresh invoices to get updated data
        await fetchInvoices();
      } else {
        alert('Failed to process immediate payment: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error processing immediate payment:', error);
      
      // CRITICAL: Check if invoice was actually paid (race condition handling)
      // If backend says "already paid", refresh invoice status to confirm
      if (error.response?.data?.code === 'ALREADY_PAID' || 
          error.response?.data?.error === 'Invoice already paid' ||
          error.response?.data?.message?.includes('already been paid')) {
        // Refresh invoice list to get latest status
        await fetchInvoices();
        
        // Show user-friendly message
        const message = error.response?.data?.message || 
                       'This invoice has already been paid. The payment is being processed and will update shortly. Please refresh the page in a few moments.';
        alert(`ℹ️ ${message}`);
        return; // Exit early - don't show error
      }
      
      // Handle specific error cases with user-friendly messages
      if (error.response?.data?.error) {
        // Backend already provided user-friendly error message
        alert(`❌ ${error.response.data.error}${error.response.data.details ? '\n' + error.response.data.details : ''}`);
      } else if (error.response?.status === 400) {
        if (error.response.data.message?.includes('linked bank account')) {
          alert('You must have a linked bank account to pay invoices. Please link your bank account in Settings first.');
        } else if (error.response.data.message?.includes('No payment method')) {
          alert('No payment method found. Please update your payment details in Settings.');
        } else {
          alert('Invoice cannot be paid: ' + error.response.data.message);
        }
      } else if (error.response?.status === 403) {
        alert('You are not authorized to pay this invoice.');
      } else if (error.response?.status === 404) {
        alert('Invoice not found or already processed.');
      } else {
        // For any other error (network, timeout, etc.), refresh invoice status
        // Payment might have succeeded in background
        console.log('⚠️ Payment error occurred - refreshing invoice status to check if payment succeeded');
        await fetchInvoices();
        alert('Payment processing encountered an issue. Please check the invoice status - the payment may have been processed successfully. If the invoice still shows as pending, please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isInvoiceSettled = (invoice) => {
    const status = (invoice?.status || '').toLowerCase();
    return ['paid', 'refunded', 'refunded_full', 'refund', 'refunded-partial', 'partially_refunded'].includes(status);
  };

  const isInvoicePaid = (invoice) => {
    return isInvoiceSettled(invoice) || 
           invoice.paymentDate || 
           invoice.paidAt || 
           invoice.isPaid ||
           (Number(invoice.amountPaid || 0) > 0 && Number(invoice.amountPaid || 0) >= Number(invoice.amount || 0));
  };

  const handleUnapprove = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for unapproval.');
      return;
    }

    if (selectedInvoice && isInvoicePaid(selectedInvoice)) {
      alert('This invoice has already been paid and cannot be unapproved.');
      return;
    }

    try {
      setLoading(true);
      const invoice = actionModal.invoice;
      console.log('🚫 Unapproving invoice with reason:', rejectReason);
      
      const response = await api.post(`/api/invoices/${invoice.id}/unapprove`, {
        reason: rejectReason
      });
      
      if (response.data.success) {
        // Dispatch event to update integrations tab immediately
        // This triggers refresh when synced invoices are deleted/voided from accounting software
        window.dispatchEvent(new CustomEvent('invoiceUnsynced', {
          detail: {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber
          }
        }));
        
        // Check if invoice has early payment discount
        const hasEarlyPaymentDiscount = invoice.discountAmount && parseFloat(invoice.discountAmount) > 0;
        const discountAmount = invoice.discountAmount;
        
        // Build contextual success message
        let successMessage = '✅ Invoice unapproved successfully.\n\n';
        successMessage += '• Payment has been cancelled\n';
        
        if (hasEarlyPaymentDiscount) {
          successMessage += `• Early payment discount cancelled (£${discountAmount})\n`;
        }
        
        successMessage += '• Supplier has been notified\n';
        successMessage += '• Supplier must submit a new invoice';
        
        // Update local state - unapprove changes status to REJECTED
        setInvoices(prev => prev.map(inv => 
          inv.id === invoice.id ? { 
            ...inv, 
            status: 'rejected',
            approvalStatus: 'rejected',
            rejectionReason: rejectReason
          } : inv
        ));
        
        setActionModal({ open: false, type: '', invoice: null });
        setSelectedInvoice(null);
        setRejectReason('');
        
        alert(successMessage);
        
        // Refresh invoices to get updated data
        await fetchInvoices();
      } else {
        alert('Failed to unapprove invoice: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error unapproving invoice:', error);
      if (error.response?.status === 400) {
        if (error.response.data.code === 'MISSING_REJECTION_REASON') {
          alert('Please provide a reason for unapproval.');
        } else if (error.response.data.code === 'REJECTION_REASON_TOO_SHORT') {
          alert('Unapproval reason is too short. Please provide at least 10 characters.');
        } else {
          alert('Invoice cannot be unapproved: ' + (error.response.data.message || 'Unknown error'));
        }
      } else if (error.response?.status === 403) {
        alert('You are not authorized to unapprove this invoice.');
      } else if (error.response?.status === 404) {
        alert('Invoice not found.');
      } else {
        alert('Error unapproving invoice. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection.');
      return;
    }

    try {
      setLoading(true);
      const invoice = actionModal.invoice;
      console.log('🚫 Rejecting invoice with reason:', rejectReason);
      const response = await api.post(`/api/invoices/${invoice.id}/reject`, {
        rejectionReason: rejectReason // Backend expects 'rejectionReason'
      });
      
      if (response.data.success) {
        // Update local state
        setInvoices(prev => prev.map(inv => 
          inv.id === invoice.id ? { 
            ...inv, 
            status: 'rejected',
            approvalStatus: 'rejected',
            rejectionReason: rejectReason
          } : inv
        ));
        
        setActionModal({ open: false, type: '', invoice: null });
        setRejectReason('');
        
        alert('Invoice rejected successfully. The supplier has been notified.');
        
        // Refresh invoices to get updated data
        await fetchInvoices();
      } else {
        alert('Failed to reject invoice: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error rejecting invoice:', error);
      if (error.response?.status === 400) {
        if (error.response.data.code === 'MISSING_REJECTION_REASON') {
          alert('Please provide a reason for rejection.');
        } else if (error.response.data.code === 'REJECTION_REASON_TOO_SHORT') {
          alert('Rejection reason is too short. Please provide at least 10 characters.');
        } else {
          alert('Invoice cannot be rejected: ' + error.response.data.message);
        }
      } else if (error.response?.status === 403) {
        alert('You are not authorized to reject this invoice.');
      } else if (error.response?.status === 404) {
        alert('Invoice not found or not pending approval.');
      } else {
        alert('Error rejecting invoice. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (invoice) => {
    const recipientEmail = invoice.guestRecipient?.email || invoice.customer?.email || 'the recipient';
    if (!window.confirm(`Resend invoice email to ${recipientEmail}?\n\nThe guest will receive the invoice details and lookup code again.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(`/invoices/${invoice.id}/resend-email`);
      
      if (response.data.success) {
        alert(`✅ Email resent successfully to ${recipientEmail}!`);
      } else {
        alert('❌ ' + (response.data.error || 'Failed to resend email'));
      }
    } catch (error) {
      console.error('Error resending email:', error);
      
      // Handle cooldown error
      if (error.response?.status === 429) {
        const cooldownMinutes = error.response?.data?.cooldownRemaining || 'some';
        alert(`⏳ Please wait ${cooldownMinutes} minute${cooldownMinutes !== 1 ? 's' : ''} before resending.\n\nThis prevents spam and ensures email deliverability.`);
      } else {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to resend email';
        alert('❌ ' + errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    // CRITICAL: Only prevent deletion of paid invoices (audit compliance)
    if (invoice.status === 'paid') {
      alert('⛔ Cannot delete paid invoices.\n\nThis invoice has been paid and cannot be deleted for audit and compliance reasons.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete invoice ${invoice.invoiceNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.delete(`/invoices/${invoice.id}`);
      
      if (response.data.success) {
        // Remove invoice from local state
        setInvoices(prev => prev.filter(inv => inv.id !== invoice.id));
        
        alert('✅ Invoice deleted successfully! The receiver will no longer see this invoice.');
        
        // Refresh invoices to get updated data
        await fetchInvoices();
      } else {
        alert('❌ Failed to delete invoice: ' + (response.data.message || response.data.error));
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error;
      if (error.response?.status === 403) {
        alert(`⛔ ${errorMessage || 'You are not authorized to delete this invoice.'}`);
      } else if (error.response?.status === 404) {
        alert('⛔ Invoice not found.');
      } else {
        alert(`❌ ${errorMessage || 'Error deleting invoice. Please try again.'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const openInvoicePreview = async (invoice) => {
    if (!invoice) {
      return;
    }

    let previewData = invoice;

    try {
      const fullInvoiceResponse = await api.get(`/invoices/${invoice.id}`);
      const fullInvoice = fullInvoiceResponse.data.invoice || fullInvoiceResponse.data.data?.invoice || fullInvoiceResponse.data;

      previewData = {
        ...invoice,
        ...fullInvoice,
        lineItems: fullInvoice.items || fullInvoice.lineItems || invoice.lineItems || [],
        sender: fullInvoice.sender || invoice.sender,
        receiver: fullInvoice.receiver || invoice.receiver,
        guestRecipient: fullInvoice.guestRecipient || invoice.guestRecipient || null,
        metadata: fullInvoice.metadata || invoice.metadata || {},
        attachments: fullInvoice.metadata?.attachments || invoice.metadata?.attachments || [],
        refundAmount: fullInvoice.refundAmount || invoice.refundAmount,
        refundStatus: fullInvoice.refundStatus || invoice.refundStatus,
        refundDate: fullInvoice.refundDate || invoice.refundDate,
        discountAmount: fullInvoice.discountAmount || invoice.discountAmount,
        discountSourceInvoice: fullInvoice.discountSourceInvoice || invoice.discountSourceInvoice,
        tax: fullInvoice.tax || invoice.tax,
        subtotal: fullInvoice.subtotal || invoice.subtotal
      };
    } catch (error) {
      console.error('Error fetching full invoice:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.error('⚠️ Auth error fetching invoice, but keeping preview open with basic data');
        alert('Unable to access full invoice details. Showing available information.');
      }
      previewData = {
        ...invoice,
        attachments: invoice.metadata?.attachments || [],
        sender: invoice.sender,
        receiver: invoice.receiver
      };
    }

    const mergedPreview = { ...previewData };

    const mergedSenderDetails = buildContactDetails(mergedPreview.sender || {});
    if (Object.keys(mergedSenderDetails).length > 0) {
      mergedPreview.sender = {
        ...(mergedPreview.sender || {}),
        ...mergedSenderDetails,
      };
    }

    const receiverSource =
      mergedPreview.receiver ||
      mergedPreview.customer ||
      mergedPreview.guestRecipient ||
      invoice.receiver ||
      invoice.customer ||
      invoice.guestRecipient;

    const mergedReceiverDetails = buildContactDetails(receiverSource || {});
    if (Object.keys(mergedReceiverDetails).length > 0) {
      const isGuestReceiver = !mergedPreview.receiver && (mergedPreview.guestRecipient || invoice.guestRecipient);

      mergedPreview.receiver = filterUndefinedFields({
        ...(mergedPreview.receiver || {}),
        ...mergedReceiverDetails,
        ...(isGuestReceiver ? { isGuest: true } : {}),
      });

      const existingCustomer = mergedPreview.customer || {};
      const updatedCustomer = {
        ...existingCustomer,
        ...mergedReceiverDetails,
      };

      if (!updatedCustomer.name) {
        updatedCustomer.name =
          mergedReceiverDetails.name ||
          existingCustomer.name ||
          mergedReceiverDetails.companyName ||
          existingCustomer.company ||
          existingCustomer.companyName ||
          receiverSource?.email ||
          'Unknown recipient';
      }

      if (!updatedCustomer.company && mergedReceiverDetails.companyName) {
        updatedCustomer.company = mergedReceiverDetails.companyName;
      }

      mergedPreview.customer = filterUndefinedFields(updatedCustomer);
    }

    const mergedGuestDetails = buildContactDetails(mergedPreview.guestRecipient || invoice.guestRecipient || {});
    if (Object.keys(mergedGuestDetails).length > 0) {
      mergedPreview.guestRecipient = filterUndefinedFields({
        ...(mergedPreview.guestRecipient || {}),
        ...mergedGuestDetails,
        isGuest: true,
      });
    }

    setSelectedInvoice(mergedPreview);
    setShowPreview(true);
  };

  const legacyHandleDownload = async (invoice) => {
    if (!invoice) {
      alert('No invoice selected');
      return;
    }
    
    try {
      console.log('📥 Downloading invoice:', invoice.invoiceNumber);
      
      // ✅ CRITICAL: Fetch full invoice data with all fields before generating PDF
      let fullInvoiceData = invoice;
      
      try {
        const fullInvoiceResponse = await api.get(`/invoices/${invoice.id}`);
        const fullInvoice = fullInvoiceResponse.data.invoice || fullInvoiceResponse.data.data?.invoice || fullInvoiceResponse.data;
        
        // Merge full data with invoice data
        fullInvoiceData = {
          ...invoice,
          ...fullInvoice,
          lineItems: fullInvoice.items || fullInvoice.lineItems || invoice.lineItems || [],
          sender: fullInvoice.sender || invoice.sender,
          receiver: fullInvoice.receiver || invoice.receiver,
          // ✅ Guest invoice address mapping
          guestRecipient: fullInvoice.guestRecipient || invoice.guestRecipient || null,
          // ✅ Metadata with attachments
          metadata: fullInvoice.metadata || invoice.metadata || {},
          attachments: fullInvoice.metadata?.attachments || invoice.metadata?.attachments || [],
          // ✅ Refund data
          refundAmount: fullInvoice.refundAmount || invoice.refundAmount,
          refundStatus: fullInvoice.refundStatus || invoice.refundStatus,
          refundDate: fullInvoice.refundDate || invoice.refundDate,
          // ✅ Discount data
          discountAmount: fullInvoice.discountAmount || invoice.discountAmount,
          discountSourceInvoice: fullInvoice.discountSourceInvoice || invoice.discountSourceInvoice,
          // ✅ Tax and subtotal
          tax: fullInvoice.tax || invoice.tax,
          subtotal: fullInvoice.subtotal || invoice.subtotal
        };
        
        console.log('✅ Full invoice data fetched for PDF:', fullInvoiceData);
      } catch (fetchError) {
        console.error('⚠️ Could not fetch full invoice data, using basic invoice data:', fetchError);
        // Continue with basic invoice data if fetch fails
      }
      
      const downloadSenderDetails = buildContactDetails(fullInvoiceData.sender || {});
      if (Object.keys(downloadSenderDetails).length > 0) {
        fullInvoiceData.sender = {
          ...(fullInvoiceData.sender || {}),
          ...downloadSenderDetails,
        };
      }

      const downloadReceiverSource =
        fullInvoiceData.receiver ||
        fullInvoiceData.customer ||
        fullInvoiceData.guestRecipient ||
        invoice.receiver ||
        invoice.customer ||
        invoice.guestRecipient;

      const downloadReceiverDetails = buildContactDetails(downloadReceiverSource || {});
      if (Object.keys(downloadReceiverDetails).length > 0) {
        const isGuestDownload = !fullInvoiceData.receiver && (fullInvoiceData.guestRecipient || invoice.guestRecipient);

        fullInvoiceData.receiver = filterUndefinedFields({
          ...(fullInvoiceData.receiver || {}),
          ...downloadReceiverDetails,
          ...(isGuestDownload ? { isGuest: true } : {}),
        });

        const existingCustomerDownload = fullInvoiceData.customer || {};
        const updatedCustomerDownload = {
          ...existingCustomerDownload,
          ...downloadReceiverDetails,
        };

        if (!updatedCustomerDownload.name) {
          updatedCustomerDownload.name =
            downloadReceiverDetails.name ||
            existingCustomerDownload.name ||
            downloadReceiverDetails.companyName ||
            existingCustomerDownload.company ||
            existingCustomerDownload.companyName ||
            downloadReceiverSource?.email ||
            'Unknown recipient';
        }

        if (!updatedCustomerDownload.company && downloadReceiverDetails.companyName) {
          updatedCustomerDownload.company = downloadReceiverDetails.companyName;
        }

        fullInvoiceData.customer = filterUndefinedFields(updatedCustomerDownload);
      }

      const downloadGuestDetails = buildContactDetails(fullInvoiceData.guestRecipient || invoice.guestRecipient || {});
      if (Object.keys(downloadGuestDetails).length > 0) {
        fullInvoiceData.guestRecipient = filterUndefinedFields({
          ...(fullInvoiceData.guestRecipient || {}),
          ...downloadGuestDetails,
          isGuest: true,
        });
      }

      // Generate PDF with complete data
      generateInvoicePDF(fullInvoiceData, invoice.invoiceNumber || 'invoice');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert(error.message || 'Failed to download invoice. Please try again.');
    }
  };

  const handleDownload = async (invoice, extraOptions = {}) => {
    if (!invoice) {
      alert('No invoice selected');
      return;
    }

    try {
      await downloadInvoice(
        invoice,
        'pdf',
        { setLoading, ...extraOptions }
      );
    } catch (error) {
      console.warn('Mobile invoices API download failed, using legacy renderer', error);
      await legacyHandleDownload(invoice);
    }
  };

  useEffect(() => {
    if (highlightQueue.current.length === 0) {
      return;
    }

    const nextHighlight = highlightQueue.current[0];

    if (nextHighlight.tab && nextHighlight.tab !== activeTab) {
      setActiveTab(nextHighlight.tab);
      return;
    }

    if (nextHighlight.tab && currentInvoicesTab.current !== nextHighlight.tab) {
      return;
    }

    if (invoices.length === 0) {
      return;
    }

    const highlightValue = extractHighlightValue(nextHighlight.id);
    const matchByNumberOnly = isNumberHighlight(nextHighlight.id);

    const targetInvoice = invoices.find((invoice) => {
      const identifiers = [
        invoice.id,
        invoice.invoiceId,
        invoice.invoiceNumber
      ]
        .filter(Boolean)
        .map((val) => String(val));

      if (matchByNumberOnly) {
        return identifiers.includes(highlightValue);
      }
      return identifiers.includes(highlightValue);
    });

    if (targetInvoice) {
      openInvoicePreview(targetInvoice);

      const newParams = new URLSearchParams(searchParams);
      newParams.delete('highlight');
      setSearchParams(newParams, { replace: true });

      highlightQueue.current.shift();
      setInitialHighlightHandled(false);
    }
  }, [invoices, activeTab, searchParams, setSearchParams]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'pending_approval': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'overdue': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'pending_approval': return <AlertTriangle className="w-4 h-4" />;
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      case 'overdue': return <AlertTriangle className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  // Filter invoices based on search and filters
  const filteredInvoiceList = invoices.filter(invoice => {
    const matchesSearch = !searchQuery || 
      invoice.invoiceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (activeTab === 'received' ? invoice.supplier?.name : invoice.customer?.name)?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (activeTab === 'received' ? invoice.supplier?.email : invoice.customer?.email)?.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesStatus = true;
    if (statusFilter !== 'all') {
      if (statusFilter === 'pending') {
        // 'pending' filter matches 'pending_approval' status
        matchesStatus = invoice.status === 'pending_approval';
      } else if (statusFilter === 'auto-approved') {
        // Handle both 'auto-approved' (filter) and 'auto_approved' (actual status)
        matchesStatus = invoice.status === 'auto_approved' || invoice.approvalStatus === 'auto_approved';
      } else {
        matchesStatus = invoice.status === statusFilter;
      }
    }
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header with Tabs */}
      <div className="bg-white sticky top-0 z-10 shadow-sm">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('received')}
            className={`flex-1 py-4 text-sm font-medium transition-colors ${
              activeTab === 'received'
                ? 'text-[#1F11ED] border-b-2 border-[#1F11ED]'
                : 'text-gray-500'
            }`}
          >
            Received
          </button>
          <button
            onClick={() => setActiveTab('sent')}
            className={`flex-1 py-4 text-sm font-medium transition-colors ${
              activeTab === 'sent'
                ? 'text-[#1F11ED] border-b-2 border-[#1F11ED]'
                : 'text-gray-500'
            }`}
          >
            Sent
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="p-4 space-y-3">
          <div className="flex items-center space-x-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg border transition-colors ${
                showFilters || statusFilter !== 'all'
                  ? 'bg-[#1F11ED] text-white border-[#1F11ED]'
                  : 'bg-white text-gray-600 border-gray-300'
              }`}
            >
              <Filter className="w-5 h-5" />
            </button>
            <button
              onClick={fetchInvoices}
              className="p-2 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="approved">Approved</option>
                  <option value="paid">Paid</option>
                  <option value="rejected">Rejected</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>
              <button
                onClick={() => {
                  setStatusFilter('all');
                  setSearchQuery('');
                  setShowFilters(false);
                }}
                className="w-full text-sm text-gray-600 py-2"
              >
                Clear Filters
              </button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Invoice List */}
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : filteredInvoiceList.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices found</h3>
            <p className="text-sm text-gray-600">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters' 
                : `No ${activeTab} invoices yet`}
            </p>
          </div>
        ) : (
          <AnimatePresence>
            {filteredInvoiceList.map((invoice, index) => (
              <motion.div
                key={invoice.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                {/* Card Header */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-lg mb-1">
                        {invoice.invoiceNumber}
                      </h3>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-600">
                          {activeTab === 'received' 
                            ? invoice.supplier?.company || invoice.supplier?.name 
                            : (invoice.guestRecipient ? 'Guest: ' : '') + (invoice.customer?.company || invoice.customer?.name)}
                        </p>
                        {/* Show Plan Badge */}
                        {activeTab === 'received' && invoice.sender?.subscriptionPlan && (
                          <PlanBadge plan={invoice.sender.subscriptionPlan} />
                        )}
                        {activeTab === 'sent' && invoice.receiver?.subscriptionPlan && (
                          <PlanBadge plan={invoice.receiver.subscriptionPlan} />
                        )}
                      </div>
                      {/* Guest Invoice Code - Only show for SENT guest invoices */}
                      {activeTab === 'sent' && invoice.guestRecipient && invoice.invoiceId && (
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-xs font-medium text-[#1F11ED] bg-blue-50 px-2 py-1 rounded">
                            Code: {invoice.invoiceId}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(invoice.invoiceId);
                              alert('Invoice code copied! Share this with your customer.');
                            }}
                            className="text-[#1F11ED] hover:text-blue-700"
                            title="Copy code to clipboard"
                          >
                            <span className="text-sm">📋</span>
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">
                        £{parseFloat(invoice.amount || 0).toFixed(2)}
                      </p>
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                        {getStatusIcon(invoice.status)}
                        <span className="capitalize">{invoice.status.replace(/_/g, ' ')}</span>
                      </span>
                    </div>
                  </div>

                  {/* Invoice Details */}
                  <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                    <div>
                      <span className="text-gray-500">Due Date:</span>
                      <p className="font-medium text-gray-900">{formatDate(invoice.dueDate)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Created:</span>
                      <p className="font-medium text-gray-900">{formatDate(invoice.createdAt)}</p>
                    </div>
                  </div>

                  {/* Early Payment Banner (Mobile Compact) */}
                  <div className="mb-4">
                    <EarlyPaymentBanner 
                      invoice={invoice} 
                      viewType={activeTab === 'received' ? 'receiver' : 'sender'} 
                      compact={true}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openInvoicePreview(invoice)}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="text-sm font-medium">View</span>
                    </button>
                    
                    <button
                      onClick={() => handleDownload(invoice)}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      <span className="text-sm font-medium">Download</span>
                    </button>

                    {/* Approval buttons for received invoices */}
                    {activeTab === 'received' && invoice.approvalStatus === 'pending' && !isInvoiceSettled(invoice) && (
                      <>
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setActionModal({ open: true, type: 'approve', invoice });
                          }}
                          disabled={approvingInvoices.has(invoice.id)}
                          className={`p-2 rounded-lg transition-colors ${
                            approvingInvoices.has(invoice.id)
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                          title={approvingInvoices.has(invoice.id) ? 'Approval in progress...' : 'Approve Invoice'}
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setActionModal({ open: true, type: 'reject', invoice });
                          }}
                          className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      </>
                    )}

                    {/* Unapprove button for approved but unpaid invoices */}
                    {activeTab === 'received' && 
                     (invoice.approvalStatus === 'approved' || invoice.status === 'approved') && 
                     !isInvoicePaid(invoice) && (
                      <button
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setActionModal({ open: true, type: 'unapprove', invoice });
                        }}
                        className="p-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                        title="Unapprove Invoice"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    )}

                    {/* Resend Email and Delete buttons for sent invoices */}
                    {activeTab === 'sent' && (
                      <>
                        {/* Resend Email Button - Guest Invoices Only */}
                        {invoice.isGuestInvoice && (
                          <button
                            onClick={() => handleResendEmail(invoice)}
                            disabled={loading}
                            className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Resend Email"
                          >
                            <Send className="w-5 h-5" />
                          </button>
                        )}

                        {/* Delete Invoice Button - Only for non-paid invoices */}
                        {invoice.status !== 'paid' && (
                          <button
                            onClick={() => handleDeleteInvoice(invoice)}
                            disabled={loading}
                            className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete Invoice"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Invoice Preview Modal */}
      <MobileInvoicePreview 
        invoice={selectedInvoice}
        isOpen={showPreview}
        onClose={() => {
          setShowPreview(false);
          setSelectedInvoice(null);
        }}
        onDownloadPDF={() => handleDownload(selectedInvoice, { forcePrintablePreview: true })}
        title="Invoice Preview"
        viewType={activeTab === 'received' ? 'receiver' : 'sender'}
      />

      {/* Action Modals */}
      {actionModal.open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-sm"
          >
            {actionModal.type === 'approve' && (() => {
              // Calculate if invoice is overdue
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const dueDate = new Date(selectedInvoice?.dueDate);
              dueDate.setHours(0, 0, 0, 0);
              const isOverdue = dueDate.getTime() < today.getTime();
              const isDueToday = dueDate.getTime() === today.getTime();
              
              // Check Stripe minimum amount
              const minCheck = checkStripeMinimum(selectedInvoice?.amount, selectedInvoice?.currency);
              const isBelowMinimum = minCheck.isBelowMinimum;
              
              const formatCurrency = (amount) => {
                return `£${parseFloat(amount || 0).toFixed(2)}`;
              };
              
              const formatDate = (date) => {
                if (!date) return 'N/A';
                return new Date(date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                });
              };
              
              return (
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Approve Invoice & Choose Payment Option</h3>
                  </div>
                  
                  {/* Overdue Warning Banner */}
                  {isOverdue && (
                    <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4 mb-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <AlertTriangle className="w-6 h-6 text-orange-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-orange-900 mb-1">⚠️ Overdue Invoice</p>
                          <p className="text-xs text-orange-700">
                            This invoice was due on <strong>{formatDate(selectedInvoice?.dueDate)}</strong> and is now <strong>overdue</strong>. Payment will be collected <strong>immediately</strong> upon approval.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Invoice Details */}
                  <div className="bg-gray-50 p-4 rounded-lg mb-6">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Invoice:</span>
                        <span className="font-medium ml-2">{selectedInvoice?.invoiceNumber}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-medium ml-2">{formatCurrency(selectedInvoice?.amount)}</span>
                      </div>
                      {selectedInvoice?.poNumber && (
                        <div>
                          <span className="text-gray-600">PO #:</span>
                          <span className="font-medium ml-2">{selectedInvoice.poNumber}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-600">Due Date:</span>
                        <span className={`font-medium ml-2 ${isOverdue ? 'text-orange-600 font-bold' : ''}`}>
                          {formatDate(selectedInvoice?.dueDate)}
                          {isOverdue && <span className="ml-1 text-xs">(OVERDUE)</span>}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Supplier:</span>
                        <span className="font-medium ml-2">{selectedInvoice?.supplier?.name || selectedInvoice?.sender?.name || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Options Section */}
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">Choose Payment Option</h4>
                    <p className="text-xs text-gray-600 mb-4">
                      Select how you'd like to pay this invoice of <strong>{formatCurrency(selectedInvoice?.amount)}</strong>
                    </p>
                    
                    {/* Minimum Amount Warning */}
                    {isBelowMinimum && (
                      <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-red-900 mb-1">⚠️ Amount Below Minimum</p>
                            <p className="text-xs text-red-700">
                              {getMinimumAmountError(selectedInvoice?.amount, selectedInvoice?.currency)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    {/* Option 1: Pay Now with Saved Card */}
                    <button
                      onClick={handlePayNow}
                      disabled={loading || isBelowMinimum}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={isBelowMinimum ? getMinimumAmountError(selectedInvoice?.amount, selectedInvoice?.currency) : ''}
                    >
                      <PoundSterling className="w-5 h-5" />
                      <span>{loading ? 'Processing...' : 'Pay Now (Saved Card)'}</span>
                    </button>
                    
                    {/* Option 2: Pay with Different Card */}
                    <button
                      onClick={async () => {
                        try {
                          setLoading(true);
                          const response = await api.post('/api/invoices/create-checkout', {
                            invoiceId: selectedInvoice.id
                          });
                          
                          if (response.data.success && response.data.url) {
                            // Redirect to Stripe Checkout
                            window.location.href = response.data.url;
                          } else {
                            alert('Failed to create checkout session. Please try again.');
                          }
                        } catch (error) {
                          console.error('Error creating checkout session:', error);
                          alert(error.response?.data?.error || 'Failed to create checkout session. Please try again.');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading || isBelowMinimum}
                      className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 font-medium flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={isBelowMinimum ? getMinimumAmountError(selectedInvoice?.amount, selectedInvoice?.currency) : ''}
                    >
                      <CreditCard className="w-5 h-5" />
                      <span>{loading ? 'Loading...' : 'Pay with Different Card'}</span>
                    </button>
                    
                    {/* Option 3: Approve for Due Date (only if not overdue) */}
                    {!isOverdue && (
                      <button
                        onClick={handleApprove}
                        disabled={loading || isBelowMinimum || (selectedInvoice && approvingInvoices.has(selectedInvoice.id))}
                        className={`w-full px-4 py-3 rounded-lg transition-colors duration-200 font-medium flex items-center justify-center space-x-2 ${
                          (loading || (selectedInvoice && approvingInvoices.has(selectedInvoice.id)))
                            ? 'bg-gray-400 text-white cursor-not-allowed opacity-50'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title={isBelowMinimum ? getMinimumAmountError(selectedInvoice?.amount, selectedInvoice?.currency) : 
                               (selectedInvoice && approvingInvoices.has(selectedInvoice.id)) ? 'Approval in progress...' : ''}
                      >
                        <CheckCircle className="w-5 h-5" />
                        <span>
                          {(loading || (selectedInvoice && approvingInvoices.has(selectedInvoice.id))) 
                            ? 'Processing...' 
                            : isDueToday 
                              ? 'Approve & Pay Today' 
                              : 'Approve for Due Date'}
                        </span>
                      </button>
                    )}
                    
                    {/* Cancel Button */}
                    <button
                      onClick={() => setActionModal({ open: false, type: '', invoice: null })}
                      disabled={loading}
                      className="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              );
            })()}

            {actionModal.type === 'reject' && (
              <>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Reject Invoice?
                </h3>
                <p className="text-sm text-gray-600 text-center mb-4">
                  Please provide a detailed reason for rejecting {actionModal.invoice?.invoiceNumber}. The supplier will be notified.
                </p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g., Incorrect pricing, wrong quantities, duplicate invoice, etc. (minimum 10 characters)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 min-h-[100px] focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 text-center mb-4">
                  {rejectReason.trim().length < 10 
                    ? `At least 10 characters needed (${rejectReason.trim().length}/10)`
                    : `${rejectReason.trim().length} characters`
                  }
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setActionModal({ open: false, type: '', invoice: null });
                      setRejectReason('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={!rejectReason.trim() || rejectReason.trim().length < 10}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reject
                  </button>
                </div>
              </>
            )}

            {actionModal.type === 'unapprove' && (
              <>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Unapprove Invoice?
                </h3>
                <p className="text-sm text-gray-600 text-center mb-4">
                  Please provide a detailed reason for unapproving {actionModal.invoice?.invoiceNumber}. The supplier will be notified and payment will be cancelled.
                </p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g., Need to review details, incorrect amount, etc. (minimum 10 characters)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 min-h-[100px] focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 text-center mb-4">
                  {rejectReason.trim().length < 10 
                    ? `At least 10 characters needed (${rejectReason.trim().length}/10)`
                    : `${rejectReason.trim().length} characters`
                  }
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setActionModal({ open: false, type: '', invoice: null });
                      setRejectReason('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUnapprove}
                    disabled={!rejectReason.trim() || rejectReason.trim().length < 10 || loading}
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Processing...' : 'Unapprove'}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MobileInvoices;

